import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import { useMe } from "@/api/hooks";

/** Custom LLM providers (`app/api/llm_providers.py`). The key and header
 *  values are write-only: reads carry `has_key` and `header_names`. */

export type LlmApiFormat = "openai-responses" | "openai-chat" | "anthropic-messages";
export const LLM_API_FORMATS: readonly LlmApiFormat[] = ["openai-responses", "openai-chat", "anthropic-messages"];
export type LlmProviderScope = "org" | "platform";

export type LlmProviderModelView = {
  slug: string;
  display_name: string | null;
  context_window: number | null;
  supports_tools: boolean | null;
  /** USD micros per 1M tokens. */
  input_price_per_mtok: number;
  output_price_per_mtok: number;
  cached_input_price_per_mtok: number | null;
  enabled: boolean;
};

export type LlmProviderView = {
  id: string;
  scope: "platform" | "org" | "user";
  org_id: string | null;
  name: string;
  api_format: LlmApiFormat;
  kind: "remote" | "local";
  base_url: string | null;
  has_key: boolean;
  header_names: string[];
  allow_private_network: boolean;
  enabled: boolean;
  last_test_at: string | null;
  last_test_ok: boolean | null;
  last_test_detail: string | null;
  created_at: string;
  updated_at: string;
  models: LlmProviderModelView[];
};

export type LlmProviderModelInput = Omit<LlmProviderModelView, "supports_tools"> & {
  supports_tools?: boolean | null;
};

export type LlmProviderPayload = {
  name?: string;
  api_format?: LlmApiFormat;
  base_url?: string;
  /** Omit to keep the stored key; "" removes it. */
  api_key?: string;
  /** Omit to keep the stored headers; {} removes them. */
  headers?: Record<string, string>;
  models?: LlmProviderModelInput[];
  enabled?: boolean;
  allow_private_network?: boolean;
};

export type LlmProbeResult = {
  ok: boolean;
  model: string | null;
  completion_ok: boolean;
  tools_ok: boolean | null;
  latency_ms: number | null;
  detail: string;
  /** True when the row changed (edited, deleted, or had its models
   *  replaced) while this probe's upstream I/O was in flight: the result
   *  shown is still what the probe measured, but it was never written
   *  to the row because it no longer describes it. Always present on
   *  the wire (``ProbeView.stale``); optional here only so literals in
   *  tests that predate it still type-check. */
  stale?: boolean;
};

const base = (scope: LlmProviderScope, orgSlug: string) =>
  scope === "platform" ? "/admin/llm-providers" : `/orgs/${orgSlug}/llm-providers`;

export const llmProvidersKey = (scope: LlmProviderScope, orgSlug: string) =>
  ["llm-providers", scope, scope === "platform" ? "" : orgSlug] as const;

export function useLlmProviders(scope: LlmProviderScope, orgSlug: string) {
  return useQuery({
    queryKey: llmProvidersKey(scope, orgSlug),
    queryFn: async () => (await api.get<LlmProviderView[]>(base(scope, orgSlug))).data,
    enabled: scope === "platform" || Boolean(orgSlug),
    staleTime: 30_000,
  });
}

export function useCreateLlmProvider(scope: LlmProviderScope, orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: LlmProviderPayload) =>
      (await api.post<LlmProviderView>(base(scope, orgSlug), payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: llmProvidersKey(scope, orgSlug) }),
  });
}

export function useUpdateLlmProvider(scope: LlmProviderScope, orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: LlmProviderPayload & { id: string }) =>
      (await api.patch<LlmProviderView>(`${base(scope, orgSlug)}/${id}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: llmProvidersKey(scope, orgSlug) }),
  });
}

export function useDeleteLlmProvider(scope: LlmProviderScope, orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${base(scope, orgSlug)}/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: llmProvidersKey(scope, orgSlug) }),
  });
}

export function useTestLlmProvider(scope: LlmProviderScope, orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, model }: { id: string; model?: string }) =>
      (await api.post<LlmProbeResult>(`${base(scope, orgSlug)}/${id}/test`, model ? { model } : {})).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: llmProvidersKey(scope, orgSlug) }),
  });
}

/** A provider the model picker may offer a member of an org (FW-19 phase
 *  2): what ``GET /orgs/{slug}/llm-providers/available`` returns — the
 *  key, base URL and prices never ride along. */
export type AvailableLlmProvider = {
  id: string;
  name: string;
  scope: "platform" | "org" | "user";
  api_format: LlmApiFormat;
  /** The agent CLI that drives this format ("codex" | "claude_code"). */
  cli: string;
  /** Always true for a platform/org (remote) provider. For a ``scope:
   *  "user"`` local provider it reflects whether the owner's connector is
   *  attached right now — the row still lists while offline (FW-19 phase
   *  3), and the new-session picker shows it disabled with that hint
   *  instead of hiding it. */
  online: boolean;
  models: {
    slug: string;
    display_name: string | null;
    context_window: number | null;
    supports_tools: boolean | null;
  }[];
};

/** How often a list that shows a local model's online state is asked
 *  again (FW-19 phase 3): the connector comes and goes while the page is
 *  open. Shared by the Account page's local-models poll. */
export const LOCAL_MODELS_POLL_MS = 15_000;

/** ``/available`` polls only while it lists a local (``scope: "user"``)
 *  model, so its online flag follows the connector; a list of remote
 *  providers only never changes on its own and isn't polled. */
export function availableRefetchInterval(data: readonly AvailableLlmProvider[] | undefined): number | false {
  return data?.some((p) => p.scope === "user") ? LOCAL_MODELS_POLL_MS : false;
}

/** Custom models a member may pick for a new session in ``orgSlug``.
 *  The route sits behind the server's feature flag (404 while off), so
 *  it is only asked when ``/me`` reports the flag on; ``data`` stays
 *  undefined otherwise and the picker shows no Custom group. */
export function useAvailableLlmProviders(orgSlug: string | null | undefined) {
  const flagOn = Boolean(useMe().data?.features?.llm_providers_enabled);
  return useQuery({
    queryKey: ["llm-providers", "available", orgSlug ?? ""] as const,
    queryFn: async () =>
      (await api.get<AvailableLlmProvider[]>(`/orgs/${orgSlug}/llm-providers/available`)).data,
    enabled: flagOn && Boolean(orgSlug),
    staleTime: 60_000,
    refetchInterval: (query) => availableRefetchInterval(query.state.data),
  });
}
